import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RewardsPanel from '../components/RewardsPanel';

const FEATURES = [
  {
    icon: '👆',
    title: 'One-Tap Chain Reactions',
    desc: 'Every level is solved with a single tap. Find the right tile and watch the whole board cascade.',
  },
  {
    icon: '🧩',
    title: '70+ Handcrafted Levels',
    desc: 'Locks, hardened cores, and ever-widening boards that test your read of the puzzle, not your reflexes.',
  },
  {
    icon: '🪙',
    title: 'Reward Vault',
    desc: 'Earn Gold Coins, Quantum Shards, Galaxy Keys, Research Points, and Time Pods as you clear boards.',
  },
  {
    icon: '🔥',
    title: 'Chain Counter',
    desc: 'Watch your combo climb in real time. Bigger chains, bigger scores, bigger bragging rights.',
  },
  {
    icon: '☁️',
    title: 'Synced Progress',
    desc: 'Sign in once and your levels, coins, and rewards follow you across every device.',
  },
  {
    icon: '⚡',
    title: '10-Second Sessions',
    desc: 'Learn it in seconds, replay it forever. Perfect for a quick break or a long streak.',
  },
];

const STEPS = [
  { step: '1', title: 'Study the board', desc: 'Every tile shows how many hits it needs. Read the pattern before you touch anything.' },
  { step: '2', title: 'Tap once', desc: 'One tap starts the chain. From there, it is entirely out of your hands.' },
  { step: '3', title: 'Watch it cascade', desc: 'Pops trigger pops. Clear the board in one tap for a perfect score.' },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Home() {
  const { user, isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen" style={{ background: 'var(--brand-bg)' }}>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200/60">
        <div
          className="pointer-events-none absolute inset-0 opacity-90"
          style={{ background: 'radial-gradient(60% 60% at 15% 10%, rgba(8,145,178,0.16) 0%, rgba(8,145,178,0) 60%), radial-gradient(50% 50% at 90% 20%, rgba(13,148,136,0.16) 0%, rgba(13,148,136,0) 60%)' }}
        />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="pill">{isAuthenticated ? `${greeting()}, ${user?.name || user?.email || 'friend'}` : 'One-Tap Puzzle Game'}</span>
              <h1 className="mt-4 text-4xl font-black leading-tight text-slate-900 sm:text-5xl">
                Tap once.
                <br />
                Watch it all cascade.
              </h1>
              <p className="mt-4 max-w-md text-base text-slate-500 sm:text-lg">
                CascadeJoy is a one-tap chain-reaction puzzle. Find the perfect tile, trigger the cascade,
                and clear the entire board before it stops.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/download" className="btn btn-primary">
                  📲 Get the app
                </Link>
                {isAuthenticated ? (
                  <Link to="/profile" className="btn" style={{ background: '#fff', border: '1.5px solid var(--brand-border)', color: 'var(--brand-ink)' }}>
                    View my progress
                  </Link>
                ) : (
                  <Link to="/register" className="btn" style={{ background: '#fff', border: '1.5px solid var(--brand-border)', color: 'var(--brand-ink)' }}>
                    Create a free account
                  </Link>
                )}
              </div>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm">
                <div>
                  <p className="text-xl font-black text-slate-900">70+</p>
                  <p className="text-slate-500">Levels</p>
                </div>
                <div>
                  <p className="text-xl font-black text-slate-900">1</p>
                  <p className="text-slate-500">Tap to clear</p>
                </div>
                <div>
                  <p className="text-xl font-black text-slate-900">5</p>
                  <p className="text-slate-500">Reward types</p>
                </div>
              </div>
            </div>

            <div className="mx-auto w-full max-w-sm">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/10">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-black uppercase tracking-widest text-[var(--brand-primary-dark)]">Level 27</span>
                  <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-black text-amber-800">CHAIN x41</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {['🟩2', '🟥1', '🟨3', '🟦2', '🟥2', '🟨1', '🟦3', '🟩1', '🟨2', '🟦2', '🟥1', '🟩3', '🟦1', '🟩2', '🟨2', '🟥1'].map((t, i) => (
                    <div
                      key={i}
                      className="flex aspect-square items-center justify-center rounded-xl text-sm font-black text-white shadow-sm"
                      style={{ background: 'var(--brand-primary)', opacity: 0.55 + (i % 5) * 0.09 }}
                    >
                      {t}
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-center text-xs font-bold text-slate-400">One tap. Zero tiles left. Perfect clear.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        {isAuthenticated && (
          <div className="mb-12">
            <RewardsPanel />
          </div>
        )}

        {/* Features */}
        <section aria-labelledby="features-title">
          <div className="mb-8 text-center">
            <span className="pill">Game Features</span>
            <h2 id="features-title" className="page-title section-gap">Everything that makes it click</h2>
            <p className="page-subtitle mx-auto max-w-xl">
              Simple to learn, hard to put down — built for short sessions and satisfying chain reactions.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-xl" style={{ background: 'var(--brand-bg)' }}>
                  {f.icon}
                </div>
                <p className="font-black text-slate-900">{f.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="mt-16" aria-labelledby="how-it-works-title">
          <div className="mb-8 text-center">
            <span className="pill">How to play</span>
            <h2 id="how-it-works-title" className="page-title section-gap">Anticipation. Chaos. Satisfaction.</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.step} className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                <div
                  className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full text-base font-black text-white"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)' }}
                >
                  {s.step}
                </div>
                <p className="font-black text-slate-900">{s.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Download CTA */}
        <section
          className="mt-16 overflow-hidden rounded-3xl border border-slate-200 px-6 py-10 text-center shadow-sm sm:px-10"
          style={{ background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)' }}
        >
          <h2 className="text-2xl font-black text-white sm:text-3xl">Ready to cascade?</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-white/85 sm:text-base">
            Play CascadeJoy free on your phone. Your rewards and progress sync automatically when you sign in.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/download" className="rounded-xl bg-white px-5 py-2.5 text-sm font-black text-[var(--brand-primary-dark)] shadow-md transition hover:-translate-y-px">
              Download for Android &amp; iOS
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
